// #37 Ring: a progress ring in three modes. `pct` draws a percentage (a gate.ring, Search Readiness); `n` with `of`
// draws n/N (a gate count); `n` alone is a numeric score out of 100 (Req health). With a `band` the arc takes that
// band's text twin (DC-17, DC-40: state strokes hold 3:1 on white); without one it is the blue → purple gradient, whose
// ids come from useSvgId so url(#…) resolves. The ring is one image: its accessible name is required and carries the
// value; the drawn glyph and sub line are hidden from assistive technology. It never animates. A glyph of three
// characters or more (three digits, a percent, n/N) never draws below 36 px (HO-23); since F-184 (26 Oct, "Same size
// progress circles all over") every ring draws at one size, 56 px, whatever size its caller asks. A glyph of four
// characters or more ("100%", "10/12") takes the ring's long face (mc-ring-long), which fits inside the stroke where the
// ring's own face would cross it (UAT F-89: "100%" on 18 px is wider than the 56 px ring's inside, on 13 px than the
// 44 px ring's).

import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useSvgId } from "../lib/useSvgId";

export type RingBand = "ok" | "info" | "warn" | "flag" | "red";
export type RingSize = 28 | 44 | 56 | 72;

export interface RingProps {
  /** The accessible name, with the value in it ("Req health 72, Yellow: 3 drivers", "Search Readiness 60%"). */
  label: string;
  /** Percent mode: 0–100, glyph "60%". */
  pct?: number | undefined;
  /** Numeric mode: a score out of 100 (glyph "72"), or with `of` a count (glyph "3/5"). */
  n?: number | undefined;
  of?: number | undefined;
  /** The arc's band (its -text twin); default the gradient. */
  band?: RingBand | undefined;
  /** Kept for the callers; every ring draws at RING_SIZE (56) since F-184. */
  size?: RingSize | undefined;
  /** A 10.5 px line under the glyph. */
  sub?: string | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

const STROKE: Readonly<Record<RingSize, number>> = { 28: 3, 44: 4, 56: 5, 72: 6 };
/** The glyph length from which the long face draws. */
const LONG_GLYPH = 4;
const clamp = (value: number) => Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));

/** The ring's arc fraction (0–100) and glyph. */
export function ringValue({ pct, n, of }: Pick<RingProps, "pct" | "n" | "of">): { arc: number; glyph: string } {
  if (pct !== undefined) return { arc: clamp(pct), glyph: `${Math.round(clamp(pct))}%` };
  if (n !== undefined && of !== undefined) return { arc: of > 0 ? clamp((n / of) * 100) : 0, glyph: `${n}/${of}` };
  if (n !== undefined) return { arc: clamp(n), glyph: String(Math.round(n)) };
  return { arc: 0, glyph: "" };
}

/** F-184 (26 Oct): "Same size progress circles all over." Every ring draws at this one size. */
export const RING_SIZE = 56 as const satisfies RingSize;

/** The size a ring draws at: always RING_SIZE, whatever size was asked or how long the glyph is (F-184; before it a
 * list-row ring drew at 28 and grew to 44 for a three-character glyph, so rings under 100 were drawn smaller). */
export function ringSize(_size: RingSize, _glyph: string): RingSize {
  return RING_SIZE;
}

export function Ring({ label, pct, n, of, band, size: asked = 72, sub, hs, className }: RingProps) {
  const id = `${useSvgId()}-rg`;
  const hot = useHs(hs);
  const { arc, glyph } = ringValue({ pct, n, of });
  const size = ringSize(asked, glyph);
  const sw = STROKE[size];
  const centre = size / 2;
  const r = (size - sw) / 2 - 1;
  const c = 2 * Math.PI * r;
  return (
    <span className={cx("mc-ring", `mc-ring-${size}`, className)} role="img" aria-label={label} {...hot}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
        {band ? null : (
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" className="mc-ring-g0" />
              <stop offset="1" className="mc-ring-g1" />
            </linearGradient>
          </defs>
        )}
        <circle className="mc-ring-track" cx={centre} cy={centre} r={r} fill="none" strokeWidth={sw} />
        {arc > 0 ? (
          <circle
            className={cx("mc-ring-arc", band && `mc-ring-${band}`)}
            cx={centre} cy={centre} r={r} fill="none" strokeWidth={sw} strokeLinecap="round"
            stroke={band ? undefined : `url(#${id})`}
            strokeDasharray={`${((c * arc) / 100).toFixed(2)} ${c.toFixed(2)}`}
          />
        ) : null}
      </svg>
      <span className={cx("mc-ring-t", glyph.length >= LONG_GLYPH && "mc-ring-long")} aria-hidden="true">
        <b>{glyph}</b>
        {sub && size >= 56 ? <span>{sub}</span> : null}
      </span>
    </span>
  );
}
